import { type ActionIdRegistry, stringUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { withErrorBoundary, withSuspense } from '@festina-life/ui/libs'
import type { CommandActionVariantOrSimulation } from '@festina-life/ui/theme'
import { useEffect } from 'react'

import { getActionMetadata } from '../../hooks/navigation/categories/utils'
import { useComponent, useService } from '../../lenteHooks'
import { LifeErrorFallback } from '../errorFallback/LifeErrorFallback'
import { useActionsContext } from './actionsContext'
import type { ActionData } from './types'
import { isActionIdWorkflow } from './utils'

type Props = {
  actionId: keyof ActionIdRegistry
  title: string
  registrationId: string
  entityId?: unknown
  isActionAvailable: boolean
  commandActionVariant: CommandActionVariantOrSimulation
}

const CommandModalButtonRegisterInternal = ({
  actionId,
  title,
  registrationId,
  entityId,
  isActionAvailable,
  commandActionVariant,
}: Props) => {
  const translate = useTranslate()
  const { onRegister: onRegisterAction, onUnregister: onUnregisterAction } =
    useActionsContext()
  const actionMetadata = useService('logic.actionMetadata')
  const metadata = getActionMetadata(actionMetadata, actionId)
  const pretendCommands = useComponent(
    'pages.commandDataActions.life.collectionPersonPretendCommandPaletteButtons',
  )
  const isPretend =
    actionId.startsWith('life.') &&
    actionId.slice('life.'.length) in pretendCommands

  const warnings = metadata.useCreateWarnings?.(entityId)
  const categories = metadata.categories

  useEffect(() => {
    const status: ActionData['status'] = isActionAvailable
      ? { isAvailable: true, warnings }
      : {
          isAvailable: false,
          warnings,
          reasons: [{ code: 'insufficientAccess' }],
        }

    onRegisterAction({
      key: registrationId,
      translation: isPretend
        ? `${title} (${translate('command.isScenario')})`
        : title,
      categories,
      nestedStatus: entityId ? { entityId, status } : undefined,
      status,
      isVisible: stringUtils.strictIncludes(commandActionVariant, [
        'create',
        'simulationOnly',
      ]),
      actionId:
        commandActionVariant === 'simulationOnly'
          ? `simulationOnly.${actionId}`
          : actionId,
      entityId,
      isWorkflow: isActionIdWorkflow(actionId),
    })
  }, [
    entityId,
    onRegisterAction,
    actionId,
    registrationId,
    commandActionVariant,
    isActionAvailable,
    title,
    warnings,
    categories,
    isPretend,
    translate,
  ])

  // The unregister is on purpose seperate as if it was part of the other useEffect, the unregister -> register would run infinite
  useEffect(
    () => () => {
      onUnregisterAction(registrationId)
    },
    [onUnregisterAction, registrationId],
  )

  return null
}

export const CommandModalButtonRegister = withErrorBoundary(
  withSuspense(CommandModalButtonRegisterInternal),
  {
    FallbackComponent: LifeErrorFallback,
  },
)
